import { toDb } from './dsp';
import type { AnalysisFrame } from './frame-analyzer';

/**
 * Onsets from spectral flux (plan doc 11).
 *
 * Flux alone fires on any brightening, so an onset also has to come with a rise
 * in level; and a refractory period stops one strum reporting four times as its
 * strings arrive.
 */

export interface Onset {
  /** Seconds on the audio clock. */
  t: number;
  strength: number;
  /**
   * Best-effort strum direction from the centroid's movement. Labels the strum
   * trainer's D and U; never used to pass or fail, because it is a guess.
   */
  direction: 'down' | 'up' | null;
}

export interface OnsetDetectorOptions {
  /** Flux must exceed the running mean by this many standard deviations. */
  sensitivity?: number;
  /** Minimum rise in level, in dB, across the last few frames. */
  riseDb?: number;
  refractoryMs?: number;
  /** Frames of history the running statistics use. */
  historyFrames?: number;
}

const DEFAULTS = {
  sensitivity: 2.5,
  riseDb: 6,
  refractoryMs: 90,
  historyFrames: 40,
} as const;

export class OnsetDetector {
  private readonly sensitivity: number;
  private readonly riseDb: number;
  private readonly refractoryMs: number;
  private readonly historyFrames: number;

  private flux: number[] = [];
  private levels: number[] = [];
  private centroids: number[] = [];
  private lastOnsetMs = -Infinity;

  constructor(options: OnsetDetectorOptions = {}) {
    this.sensitivity = options.sensitivity ?? DEFAULTS.sensitivity;
    this.riseDb = options.riseDb ?? DEFAULTS.riseDb;
    this.refractoryMs = options.refractoryMs ?? DEFAULTS.refractoryMs;
    this.historyFrames = options.historyFrames ?? DEFAULTS.historyFrames;
  }

  reset(): void {
    this.flux = [];
    this.levels = [];
    this.centroids = [];
    this.lastOnsetMs = -Infinity;
  }

  /** Feeds one frame; returns an onset when this frame is one. */
  push(frame: AnalysisFrame): Onset | null {
    const tMs = frame.t * 1000;
    const priorFlux = [...this.flux];

    this.remember(this.flux, frame.flux);
    this.remember(this.levels, frame.rms);
    this.remember(this.centroids, frame.centroid);

    // Needs enough history for the statistics to mean anything.
    if (priorFlux.length < 4) return null;
    if (tMs - this.lastOnsetMs < this.refractoryMs) return null;

    const { mean, std } = stats(priorFlux);
    const threshold = mean + this.sensitivity * std;
    if (frame.flux <= threshold || frame.flux <= 0) return null;

    // The level must have risen too: flux alone fires whenever a note merely
    // gets brighter, which a sustained note does as its attack settles.
    const before = this.levels[Math.max(0, this.levels.length - 4)] ?? frame.rms;
    if (toDb(frame.rms) - toDb(before) < this.riseDb) return null;

    this.lastOnsetMs = tMs;
    return {
      t: frame.t,
      strength: std > 0 ? (frame.flux - mean) / std : frame.flux,
      direction: this.direction(),
    };
  }

  private direction(): 'down' | 'up' | null {
    if (this.centroids.length < 3) return null;
    const recent = this.centroids.slice(-3);
    const first = recent[0] ?? 0;
    const last = recent[recent.length - 1] ?? 0;
    if (first === 0 || last === 0) return null;
    const change = (last - first) / first;
    if (Math.abs(change) < 0.05) return null;
    // A down-strum hits the low strings first, so the centroid climbs.
    return change > 0 ? 'down' : 'up';
  }

  private remember(buffer: number[], value: number): void {
    buffer.push(value);
    if (buffer.length > this.historyFrames) buffer.shift();
  }
}

function stats(values: number[]): { mean: number; std: number } {
  if (values.length === 0) return { mean: 0, std: 0 };
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length;
  return { mean, std: Math.sqrt(variance) };
}
